import { useCallback, useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getNavFor } from "@/lib/nav";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle2, Clock, Lock, Users, Download, CalendarDays, UserPlus, Trash2, Settings2, Scale, KeyRound, ChevronUp, ChevronDown, Plus, X } from "lucide-react";

const DOW = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const TILLS = ["Till 1", "Till 2", "Till 3", "Till 4", "Insurance Desk", "Calls & Deliveries", "Stock Order"];

// All roster day math is timezone-independent, anchored to the EAT date string the backend sends:
// parse YYYY-MM-DD into a UTC instant and only ever use UTC getters, so the viewer's device
// timezone can never shift the calendar day (fixes the one-day drift for Uganda/UTC+3 users).
const DAY_MS = 86400000;
function parseISO(s) { const [y, m, d] = s.split("-").map(Number); return Date.UTC(y, m - 1, d); }
function fmtUTC(ts) {
  const d = new Date(ts);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}
function weekFrom(todayStr) {
  const ts = parseISO(todayStr);
  const dow = (new Date(ts).getUTCDay() + 6) % 7; // Mon=0
  const monTs = ts - dow * DAY_MS;
  return Array.from({ length: 7 }, (_, i) => monTs + i * DAY_MS);
}

export default function RolesTracker() {
  const { user } = useAuth();
  const isDispenser = user?.role === "dispenser";
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(user?.branch_id || "");
  const [config, setConfig] = useState(null);
  const [staff, setStaff] = useState([]);
  const [entries, setEntries] = useState([]);
  const [selDate, setSelDate] = useState("");
  const [cover, setCover] = useState(false);
  const [coverStaff, setCoverStaff] = useState([]);
  const [form, setForm] = useState({ staff_id: "", staff_name: "", shift_type: "", till: "", shift_time: "", is_cover: false, home_branch_id: null });
  const [staged, setStaged] = useState([]);
  const [extraOpen, setExtraOpen] = useState(false);
  const [extraForm, setExtraForm] = useState({ staff_id: "", staff_name: "", shift_type: "", till: "", shift_time: "" });
  const [adminOpen, setAdminOpen] = useState(false);
  const [pwInput, setPwInput] = useState("");
  const [opsPw, setOpsPw] = useState(null); // verified password (or "" for super)
  const [, force] = useState(0);

  useEffect(() => { api.get("/branches").then((r) => setBranches(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (!branchId && branches.length) setBranchId(branches[0].id); }, [branches, branchId]);

  const week = useMemo(() => (config ? weekFrom(config.today) : []), [config]);

  const loadConfig = useCallback(async () => {
    if (!branchId) return;
    try {
      const { data } = await api.get(`/roles/config?branch_id=${branchId}`);
      setConfig(data);
      setSelDate((prev) => prev || data.today);
    } catch (e) { console.error(e); }
  }, [branchId]);

  const loadStaff = useCallback(async () => {
    if (!branchId) return;
    const { data } = await api.get(`/roles/staff?branch_id=${branchId}`);
    setStaff(data);
  }, [branchId]);

  const loadEntries = useCallback(async () => {
    if (!branchId || !selDate) return;
    const { data } = await api.get(`/roles/entries?branch_id=${branchId}&date=${selDate}`);
    setEntries(data);
  }, [branchId, selDate]);

  useEffect(() => { loadConfig(); loadStaff(); }, [loadConfig, loadStaff]);
  useEffect(() => { loadEntries(); }, [loadEntries]);
  // live refresh
  useEffect(() => { const t = setInterval(() => { loadEntries(); }, 15000); return () => clearInterval(t); }, [loadEntries]);
  useEffect(() => { const t = setInterval(() => force((n) => n + 1), 30000); return () => clearInterval(t); }, []);

  const isSunday = selDate && new Date(parseISO(selDate)).getUTCDay() === 0;
  const timeOptions = useMemo(() => {
    if (!config) return [];
    if (isSunday) return config.sunday_times;
    return form.shift_type === "evening" ? config.evening_times : config.day_times;
  }, [config, isSunday, form.shift_type]);

  const filledIds = useMemo(() => new Set(entries.map((e) => e.staff_id)), [entries]);
  const isFuture = selDate && config && selDate > config.today;
  const beforeWeek = selDate && week.length && selDate < fmtUTC(week[0]);
  const canFill = selDate && !isFuture && !beforeWeek;

  const openCover = async () => {
    setCover(true);
    const { data } = await api.get("/roles/staff/all");
    setCoverStaff(data.filter((s) => s.branch_id !== branchId));
  };

  const pickStaff = (s, isCov) => {
    setStaged([]);
    setForm({
      staff_id: s.id, staff_name: s.name, shift_type: "", till: "", shift_time: "",
      is_cover: !!isCov, home_branch_id: isCov ? s.branch_id : null,
    });
  };

  const addShift = () => {
    const stype = isSunday ? "day" : form.shift_type;
    if (!form.staff_id) { toast.error("Pick a staff member"); return; }
    if (!stype || !form.till || !form.shift_time) { toast.error("Choose shift type, till and time"); return; }
    const dupStaged = staged.some((s) => s.shift_type === stype && s.till === form.till);
    const dupSaved = entries.some((e) => e.staff_id === form.staff_id && e.shift_type === stype && e.till === form.till);
    if (dupStaged || dupSaved) { toast.error(`${form.till} on ${stype} is already added for ${form.staff_name}`); return; }
    const opt = timeOptions.find((o) => o.label === form.shift_time);
    setStaged((prev) => [...prev, { shift_type: stype, till: form.till, shift_time: form.shift_time, hours: opt?.hours ?? 0 }]);
    setForm((f) => ({ ...f, shift_type: "", till: "", shift_time: "" }));
  };

  const removeStaged = (idx) => setStaged((prev) => prev.filter((_, i) => i !== idx));

  const extraHasNormal = useMemo(() =>
    extraForm.staff_id && extraForm.shift_type &&
    entries.some((e) => e.staff_id === extraForm.staff_id && e.shift_type === extraForm.shift_type && !e.is_extra),
    [entries, extraForm.staff_id, extraForm.shift_type]);

  const submitExtra = async () => {
    const { staff_id, staff_name, shift_type, till, shift_time } = extraForm;
    if (!staff_id || !shift_type || !till || !shift_time) { toast.error("Complete all fields"); return; }
    if (!extraHasNormal) { toast.error(`Log the ${shift_type} shift first before adding extra time`); return; }
    try {
      await api.post("/roles/entries", { branch_id: branchId, date: selDate, staff_id, staff_name, shift_type, till, shift_time, is_extra: true });
      toast.success("Extra time logged");
      setExtraForm({ staff_id: "", staff_name: "", shift_type: "", till: "", shift_time: "" });
      setExtraOpen(false);
      loadEntries();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const submit = async () => {
    if (!form.staff_id || staged.length === 0) return;
    let ok = 0;
    const errs = [];
    for (const s of staged) {
      try {
        await api.post("/roles/entries", {
          branch_id: branchId, date: selDate, staff_id: form.staff_id, staff_name: form.staff_name,
          is_cover: form.is_cover, home_branch_id: form.home_branch_id,
          shift_type: s.shift_type, till: s.till, shift_time: s.shift_time,
        });
        ok++;
      } catch (e) { errs.push(`${s.till}: ${formatApiErrorDetail(e.response?.data?.detail)}`); }
    }
    if (ok) toast.success(`${ok} shift${ok > 1 ? "s" : ""} logged`);
    if (errs.length) toast.error(errs.join("; "));
    setStaged([]);
    setForm({ staff_id: "", staff_name: "", shift_type: "", till: "", shift_time: "", is_cover: false, home_branch_id: null });
    setCover(false);
    loadEntries();
  };

  const delEntry = async (e) => {
    try { await api.delete(`/roles/entries/${e.id}`); toast.success("Removed"); loadEntries(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const editable = (e) => new Date(e.editable_until) > new Date();
  const minsLeft = (e) => Math.max(0, Math.round((new Date(e.editable_until) - new Date()) / 60000));

  const verifyAdmin = async () => {
    try {
      await api.post("/roles/admin/verify", { branch_id: branchId, password: pwInput });
      setOpsPw(user.role === "super_admin" ? "" : pwInput);
      setAdminOpen(false);
      toast.success("Operations Desk unlocked");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const nav = getNavFor(user);
  const branchName = branches.find((b) => b.id === branchId)?.name || "";

  if (opsPw !== null) {
    return <OpsDesk user={user} nav={nav} branchId={branchId} branchName={branchName} opsPw={opsPw}
      onExit={() => setOpsPw(null)} onStaffChange={loadStaff} />;
  }

  return (
    <Layout nav={nav} showTestButton={false}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-foreground">Roles Tracker</h1>
          <p className="mt-1 text-sm text-muted-foreground">{branchName ? `${branchName} · ` : ""}log today's shifts. Today is highlighted; past days of this week are still fillable.</p>
        </div>
        <div className="flex items-center gap-2">
          {!isDispenser && (
            <Select value={branchId} onValueChange={(v) => { setBranchId(v); setSelDate(""); }}>
              <SelectTrigger className="w-48" data-testid="roles-branch-select"><SelectValue placeholder="Branch" /></SelectTrigger>
              <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Button variant="outline" onClick={() => { setExtraForm({ staff_id: "", staff_name: "", shift_type: "", till: "", shift_time: "" }); setExtraOpen(true); }} data-testid="roles-extra-button"><Clock className="mr-1.5 h-4 w-4" />Log Extra Time</Button>
          <Button variant="outline" onClick={() => { setPwInput(""); setAdminOpen(true); }} data-testid="roles-admin-button"><ShieldCheck className="mr-1.5 h-4 w-4" />Admin</Button>
        </div>
      </div>

      {/* Week rail */}
      <div className="mt-6 grid grid-cols-7 gap-2" data-testid="roles-week-rail">
        {week.map((d, i) => {
          const ds = fmtUTC(d);
          const today = config && ds === config.today;
          const future = config && ds > config.today;
          const active = ds === selDate;
          return (
            <button key={ds} disabled={future} onClick={() => setSelDate(ds)} data-testid={`roles-day-${ds}`}
              className={`rounded-xl border p-2 text-center transition-colors sm:p-3 ${active ? "border-sky-500 bg-sky-50 dark:bg-sky-950" : "border-border bg-card"} ${future ? "opacity-40 cursor-not-allowed" : "hover:border-sky-300"}`}>
              <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground sm:text-[11px]">{DOW[i].slice(0, 3)}</div>
              <div className="font-heading text-base font-bold text-foreground sm:text-lg">{new Date(d).getUTCDate()}</div>
              {today && <div className="mt-0.5 text-[9px] font-semibold text-sky-600 sm:text-[10px]">Today</div>}
            </button>
          );
        })}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Staff list + progress */}
        <div className="lg:col-span-5">
          <Card className="p-5">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-sm font-semibold text-foreground"><Users className="h-4 w-4 text-sky-600" />Branch staff</div>
              <Badge variant="outline" data-testid="roles-progress">filled {filledIds.size} of {staff.length}</Badge>
            </div>
            <div className="space-y-1.5">
              {staff.map((s) => {
                const done = filledIds.has(s.id);
                return (
                  <button key={s.id} disabled={!canFill} onClick={() => pickStaff(s, false)} data-testid={`roles-staff-${s.id}`}
                    className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm transition-colors ${form.staff_id === s.id ? "border-sky-500 bg-sky-50 dark:bg-sky-950" : done ? "border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/40" : "border-border hover:bg-muted"} ${!canFill ? "opacity-60" : ""}`}>
                    <span className="font-medium text-foreground">{s.name}</span>
                    {done ? <Badge className="gap-1 bg-emerald-100 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950 dark:text-emerald-300 text-[10px]"><CheckCircle2 className="h-3 w-3" />done</Badge>
                      : <span className="text-[11px] text-muted-foreground">pending</span>}
                  </button>
                );
              })}
            </div>
            <Button variant="outline" className="mt-3 w-full" disabled={!canFill} onClick={openCover} data-testid="roles-cover-button">
              <UserPlus className="mr-1.5 h-4 w-4" />Cover — I'm from another branch
            </Button>
            {cover && (
              <div className="mt-3 max-h-48 space-y-1 overflow-y-auto rounded-lg border border-border p-2 canda-scroll">
                {coverStaff.map((s) => (
                  <button key={s.id} onClick={() => pickStaff(s, true)} data-testid={`roles-cover-${s.id}`}
                    className={`flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted ${form.staff_id === s.id ? "bg-sky-50 dark:bg-sky-950" : ""}`}>
                    <span>{s.name}</span><span className="text-[11px] text-muted-foreground">{s.branch_name}</span>
                  </button>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Fill form + today's entries */}
        <div className="lg:col-span-7 space-y-5">
          {canFill && form.staff_id && (
            <Card className="p-5" data-testid="roles-fill-form">
              <div className="text-sm font-semibold text-foreground">Logging for <span className="text-sky-600">{form.staff_name}</span>{form.is_cover ? " (cover)" : ""} · {selDate}</div>
              {!isSunday && (
                <div className="mt-4">
                  <Label>Shift type</Label>
                  <div className="mt-1.5 flex gap-2">
                    {["day", "evening"].map((t) => (
                      <Button key={t} variant={form.shift_type === t ? "default" : "outline"} onClick={() => setForm({ ...form, shift_type: t, shift_time: "" })} data-testid={`roles-shifttype-${t}`} className="capitalize">{t}</Button>
                    ))}
                  </div>
                </div>
              )}
              {(isSunday || form.shift_type) && (
                <div className="mt-4">
                  <Label>Till</Label>
                  <Select value={form.till} onValueChange={(v) => setForm({ ...form, till: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="roles-till-select"><SelectValue placeholder="Select till" /></SelectTrigger>
                    <SelectContent>{(config?.tills || TILLS).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {form.till && (
                <div className="mt-4">
                  <Label>Shift time</Label>
                  <div className="mt-1.5 flex flex-wrap gap-2">
                    {timeOptions.map((o) => (
                      <Button key={o.label} variant={form.shift_time === o.label ? "default" : "outline"} onClick={() => setForm({ ...form, shift_time: o.label, shift_type: isSunday ? "day" : form.shift_type })} data-testid={`roles-time-${o.label}`}>
                        {o.label} <span className="ml-1 opacity-60">({o.hours}h)</span>
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              {form.shift_time && (
                <Button variant="outline" className="mt-4" onClick={addShift} data-testid="roles-add-shift">
                  <Plus className="mr-1.5 h-4 w-4" />Add shift
                </Button>
              )}

              {staged.length > 0 && (
                <div className="mt-4 space-y-2" data-testid="roles-staged-list">
                  <Label>Shifts to submit</Label>
                  <div className="flex flex-wrap gap-2">
                    {staged.map((s, i) => (
                      <span key={i} data-testid={`roles-staged-${i}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-medium text-sky-800 dark:border-sky-900 dark:bg-sky-950 dark:text-sky-200">
                        <span className="capitalize">{s.shift_type}</span> · {s.till} · {s.shift_time} ({s.hours}h)
                        <button onClick={() => removeStaged(i)} data-testid={`roles-staged-remove-${i}`}
                          className="ml-0.5 rounded-full p-0.5 transition-colors hover:bg-sky-200 dark:hover:bg-sky-800" aria-label="Remove shift">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <Button className="mt-5" onClick={submit} disabled={staged.length === 0} data-testid="roles-submit">
                Submit{staged.length ? ` ${staged.length} shift${staged.length > 1 ? "s" : ""}` : ""}
              </Button>
            </Card>
          )}
          {isFuture && <Card className="p-5 text-sm text-muted-foreground">Future days cannot be filled.</Card>}

          <Card className="p-5">
            <div className="mb-3 text-sm font-semibold text-foreground">Entries for {selDate}</div>
            {entries.length === 0 && <p className="text-sm text-muted-foreground">No entries yet.</p>}
            <div className="space-y-2">
              {entries.map((e) => (
                <div key={e.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm" data-testid={`roles-entry-${e.id}`}>
                  <div>
                    <span className="font-medium text-foreground">{e.staff_name}</span>
                    {e.is_cover && <Badge variant="outline" className="ml-1.5 text-[10px]">cover</Badge>}
                    {e.is_extra && <Badge className="ml-1.5 bg-amber-100 text-amber-700 hover:bg-amber-100 dark:bg-amber-950 dark:text-amber-300 text-[10px]">Extra Time</Badge>}
                    <div className="text-xs text-muted-foreground">{e.is_extra ? "extra · " : ""}{e.shift_type} · {e.till} · {e.shift_time} · {e.worked_hours}h</div>
                  </div>
                  {editable(e)
                    ? <div className="flex items-center gap-2"><Badge variant="outline" className="gap-1 text-[10px]"><Clock className="h-3 w-3" />{minsLeft(e)}m to edit</Badge><Button variant="ghost" size="icon" onClick={() => delEntry(e)} data-testid={`roles-del-${e.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button></div>
                    : <Badge variant="outline" className="gap-1 text-[10px] text-muted-foreground"><Lock className="h-3 w-3" />locked</Badge>}
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      <Dialog open={adminOpen} onOpenChange={setAdminOpen}>
        <DialogContent data-testid="roles-admin-dialog">
          <DialogHeader><DialogTitle className="font-heading">Operations Officer Desk</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">{branchName} — enter the branch desk password{user.role === "super_admin" ? " (super admin may leave blank)" : ""}.</p>
            <Input type="password" value={pwInput} onChange={(e) => setPwInput(e.target.value)} placeholder="Password" data-testid="roles-admin-password" onKeyDown={(e) => e.key === "Enter" && verifyAdmin()} />
          </div>
          <DialogFooter><Button onClick={verifyAdmin} data-testid="roles-admin-submit">Unlock</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={extraOpen} onOpenChange={setExtraOpen}>
        <DialogContent data-testid="roles-extra-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Clock className="h-5 w-5 text-amber-600" />Log Extra Time</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">Time worked while waiting for the next person to arrive, on top of a normal shift for {selDate}.</p>
            <div>
              <Label>Name</Label>
              <Select value={extraForm.staff_id} onValueChange={(v) => { const s = staff.find((x) => x.id === v); setExtraForm({ ...extraForm, staff_id: v, staff_name: s?.name || "", shift_time: "" }); }}>
                <SelectTrigger className="mt-1.5" data-testid="extra-staff-select"><SelectValue placeholder="Select staff" /></SelectTrigger>
                <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Shift type</Label>
              <div className="mt-1.5 flex gap-2">
                {["day", "evening"].map((t) => (
                  <Button key={t} variant={extraForm.shift_type === t ? "default" : "outline"} onClick={() => setExtraForm({ ...extraForm, shift_type: t })} data-testid={`extra-shifttype-${t}`} className="capitalize">{t}</Button>
                ))}
              </div>
              {extraForm.staff_id && extraForm.shift_type && !extraHasNormal && (
                <p className="mt-2 text-xs font-medium text-amber-700 dark:text-amber-400" data-testid="extra-needs-shift">Log the {extraForm.shift_type} shift for {extraForm.staff_name} first — extra time adds on top of a normal shift.</p>
              )}
            </div>
            <div>
              <Label>Till</Label>
              <Select value={extraForm.till} onValueChange={(v) => setExtraForm({ ...extraForm, till: v })}>
                <SelectTrigger className="mt-1.5" data-testid="extra-till-select"><SelectValue placeholder="Select till" /></SelectTrigger>
                <SelectContent>{(config?.tills || TILLS).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Extra-time length</Label>
              <Select value={extraForm.shift_time} onValueChange={(v) => setExtraForm({ ...extraForm, shift_time: v })}>
                <SelectTrigger className="mt-1.5" data-testid="extra-length-select"><SelectValue placeholder="Select length" /></SelectTrigger>
                <SelectContent>{(config?.extra_times || []).map((o) => <SelectItem key={o.label} value={o.label}>{o.label} ({o.hours}h)</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submitExtra} disabled={!extraForm.staff_id || !extraForm.shift_type || !extraForm.till || !extraForm.shift_time || !extraHasNormal} data-testid="extra-submit">Log extra time</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function OpsDesk({ user, nav, branchId, branchName, opsPw, onExit, onStaffChange }) {
  const hdr = { headers: opsPw ? { "X-Ops-Password": opsPw } : {} };
  const [staff, setStaff] = useState([]);
  const [newStaff, setNewStaff] = useState("");
  const [newTill, setNewTill] = useState("");
  const [cfg, setCfg] = useState(null);
  const [range, setRange] = useState({ start: "", end: "" });
  const [review, setReview] = useState(null);
  const [weighted, setWeighted] = useState(false);
  const [weighting, setWeighting] = useState(null);
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState({ key: "staff_name", dir: "asc" });

  const loadStaff = useCallback(async () => {
    const { data } = await api.get(`/roles/staff?branch_id=${branchId}&include_suspended=true`);
    setStaff(data);
  }, [branchId]);
  const loadCfg = useCallback(async () => { const { data } = await api.get(`/roles/config?branch_id=${branchId}`); setCfg(data); }, [branchId]);
  useEffect(() => { loadStaff(); loadCfg(); if (user.role === "super_admin") api.get("/roles/weighting").then((r) => setWeighting(r.data)); }, [loadStaff, loadCfg, user.role]);

  const addStaff = async () => {
    if (!newStaff.trim()) return;
    try { await api.post("/roles/staff", { branch_id: branchId, name: newStaff.trim() }, hdr); setNewStaff(""); loadStaff(); onStaffChange?.(); toast.success("Staff added"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleSuspend = async (s) => { await api.put(`/roles/staff/${s.id}`, { is_suspended: !s.is_suspended }, hdr); loadStaff(); onStaffChange?.(); };
  const removeStaff = async (s) => { if (!window.confirm(`Remove ${s.name}?`)) return; await api.delete(`/roles/staff/${s.id}`, hdr); loadStaff(); onStaffChange?.(); };

  const runReview = async () => {
    if (!range.start || !range.end) { toast.error("Pick a date range"); return; }
    try { const { data } = await api.get(`/roles/review?branch_id=${branchId}&start=${range.start}&end=${range.end}`, hdr); setReview(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const doExport = () => {
    if (!range.start || !range.end) { toast.error("Pick a date range"); return; }
    const pw = opsPw ? `&pw=${encodeURIComponent(opsPw)}` : "";
    window.open(`${API}/roles/export?branch_id=${branchId}&start=${range.start}&end=${range.end}&shift=${filter}${pw}`, "_blank");
  };

  const fmtBd = (bd) => { const ks = Object.keys(bd || {}); return ks.length ? ks.map((k) => `${k} · ${bd[k]}h`).join(", ") : "—"; };
  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));
  const rows = useMemo(() => {
    if (!review) return [];
    let list = review.rows.map((r) => {
      const _entries = filter === "day" ? (r.day_split.day || 0) : filter === "evening" ? (r.day_split.evening || 0) : filter === "extra" ? (r.extra_count || 0) : r.entries;
      const _hours = filter === "day" ? (weighted ? r.day_weighted_hours : r.day_hours)
        : filter === "evening" ? (weighted ? r.evening_weighted_hours : r.evening_hours)
          : filter === "extra" ? (weighted ? r.extra_weighted_hours : r.extra_hours)
            : (weighted ? r.weighted_hours : r.worked_hours);
      return { ...r, _entries, _hours };
    });
    if (filter === "day") list = list.filter((r) => (r.day_split.day || 0) > 0);
    if (filter === "evening") list = list.filter((r) => (r.day_split.evening || 0) > 0);
    if (filter === "extra") list = list.filter((r) => (r.extra_hours || 0) > 0);
    const { key, dir } = sort;
    list.sort((a, b) => {
      let av, bv;
      if (key === "staff_name") { av = a.staff_name.toLowerCase(); bv = b.staff_name.toLowerCase(); }
      else if (key === "entries") { av = a._entries; bv = b._entries; }
      else if (key === "dayeve") { av = a.day_split.day || 0; bv = b.day_split.day || 0; }
      else { av = a._hours; bv = b._hours; }
      if (av < bv) return dir === "asc" ? -1 : 1;
      if (av > bv) return dir === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [review, filter, weighted, sort]);

  const SortHead = ({ label, col, align }) => (
    <TableHead className={`cursor-pointer select-none ${align === "right" ? "text-right" : ""}`} onClick={() => toggleSort(col)} data-testid={`ops-sort-${col}`}>
      <span className={`inline-flex items-center gap-1 ${align === "right" ? "justify-end" : ""}`}>{label}
        {sort.key === col && (sort.dir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
      </span>
    </TableHead>
  );

  const saveTime = async (field, list) => {
    await api.put("/roles/settings", { branch_id: branchId, [field]: list }, hdr);
    loadCfg(); toast.success("Shift times saved");
  };
  const editTimeList = (field) => {
    const list = cfg[field] || [];
    const label = window.prompt("Add shift time label (e.g. 9AM–5PM):"); if (!label) return;
    const hours = parseFloat(window.prompt("Worked hours for this time (e.g. 8):") || "0");
    saveTime(field, [...list, { label, hours }]);
  };
  const removeTime = (field, idx) => { const list = [...(cfg[field] || [])]; list.splice(idx, 1); saveTime(field, list); };

  const saveTills = async (list) => { await api.put("/roles/settings", { branch_id: branchId, tills: list }, hdr); loadCfg(); toast.success("Tills saved"); };
  const addTill = () => {
    const t = newTill.trim(); if (!t) return;
    const list = cfg.tills || [];
    if (list.includes(t)) { toast.error("That till already exists"); return; }
    saveTills([...list, t]); setNewTill("");
  };
  const editTill = (i) => { const list = [...(cfg.tills || [])]; const v = window.prompt("Rename till:", list[i]); if (!v || !v.trim()) return; list[i] = v.trim(); saveTills(list); };
  const removeTill = (i) => { const list = [...(cfg.tills || [])]; list.splice(i, 1); saveTills(list); };

  const saveWeighting = async (scope, till, val) => {
    const next = { ...weighting, [scope]: { ...weighting[scope], [till]: val } };
    setWeighting(next);
    await api.put("/roles/weighting", { [scope]: next[scope] });
  };
  const setPassword = async () => {
    const p = window.prompt(`Set Operations Desk password for ${branchName}:`); if (!p) return;
    await api.put("/roles/admin/password", { branch_id: branchId, password: p }); toast.success("Password updated");
  };

  return (
    <Layout nav={nav} showTestButton={false}>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-foreground">Operations Desk</h1>
          <p className="mt-1 text-sm text-muted-foreground">{branchName}</p>
        </div>
        <Button variant="outline" onClick={onExit} data-testid="roles-ops-exit">Back to roster</Button>
      </div>

      <Tabs defaultValue="review" className="mt-6">
        <TabsList>
          <TabsTrigger value="review" data-testid="ops-tab-review"><CalendarDays className="mr-1.5 h-4 w-4" />Review & Export</TabsTrigger>
          <TabsTrigger value="staff" data-testid="ops-tab-staff"><Users className="mr-1.5 h-4 w-4" />Staff</TabsTrigger>
          <TabsTrigger value="times" data-testid="ops-tab-times"><Settings2 className="mr-1.5 h-4 w-4" />Shift Times</TabsTrigger>
          {user.role === "super_admin" && <TabsTrigger value="rules" data-testid="ops-tab-rules"><Scale className="mr-1.5 h-4 w-4" />Weighting</TabsTrigger>}
        </TabsList>

        <TabsContent value="review" className="mt-6">
          <Card className="p-5">
            <div className="flex flex-wrap items-end gap-3">
              <div><Label>From</Label><Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} data-testid="ops-range-start" /></div>
              <div><Label>To</Label><Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="ops-range-end" /></div>
              <Button onClick={runReview} data-testid="ops-run-review">Review</Button>
              <div className="flex items-center gap-2"><span className="text-sm">Weighted</span><Switch checked={weighted} onCheckedChange={setWeighted} data-testid="ops-weighted-toggle" /></div>
              <Button variant="outline" onClick={doExport} data-testid="ops-export"><Download className="mr-1.5 h-4 w-4" />Export .xlsx</Button>
            </div>
            {review && (
              <>
                <div className="mt-5 flex flex-wrap items-center gap-2">
                  <span className="text-xs font-medium text-muted-foreground">Show</span>
                  <div className="inline-flex rounded-lg border border-border p-0.5" data-testid="ops-shift-filter">
                    {["all", "day", "evening", "extra"].map((f) => (
                      <button key={f} onClick={() => setFilter(f)} data-testid={`ops-filter-${f}`}
                        className={`rounded-md px-3 py-1 text-xs font-medium capitalize transition-colors ${filter === f ? "bg-sky-600 text-white" : "text-muted-foreground hover:text-foreground"}`}>
                        {f === "extra" ? "Extra Time" : f}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="mt-3 overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow>
                      <SortHead label="Dispenser" col="staff_name" />
                      <SortHead label="Entries" col="entries" />
                      <SortHead label="Day / Eve" col="dayeve" />
                      <TableHead>Day Breakdown</TableHead>
                      <TableHead>Evening Breakdown</TableHead>
                      <TableHead className="text-right">Extra Time</TableHead>
                      <SortHead label={`${weighted ? "Weighted" : "Worked"} Hours`} col="hours" align="right" />
                    </TableRow></TableHeader>
                    <TableBody>
                      {rows.length === 0 && <TableRow><TableCell colSpan={7} className="py-6 text-center text-sm text-muted-foreground">No entries in range.</TableCell></TableRow>}
                      {rows.map((r) => (
                        <TableRow key={r.staff_id} data-testid={`ops-row-${r.staff_id}`}>
                          <TableCell className="font-medium">{r.staff_name}</TableCell>
                          <TableCell>{r._entries}</TableCell>
                          <TableCell>{r.day_split.day || 0} / {r.day_split.evening || 0}</TableCell>
                          <TableCell className="text-xs text-muted-foreground" data-testid={`ops-day-bd-${r.staff_id}`}>{fmtBd(weighted ? r.day_breakdown_weighted : r.day_breakdown)}</TableCell>
                          <TableCell className="text-xs text-muted-foreground" data-testid={`ops-eve-bd-${r.staff_id}`}>{fmtBd(weighted ? r.evening_breakdown_weighted : r.evening_breakdown)}</TableCell>
                          <TableCell className="text-right font-mono-c" data-testid={`ops-extra-${r.staff_id}`}>{r.extra_hours ? `${r.extra_hours}h` : "—"}</TableCell>
                          <TableCell className="text-right font-mono-c">{r._hours}h</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="staff" className="mt-6">
          <Card className="p-5">
            <div className="flex gap-2">
              <Input value={newStaff} onChange={(e) => setNewStaff(e.target.value)} placeholder="New staff name" data-testid="ops-new-staff" onKeyDown={(e) => e.key === "Enter" && addStaff()} />
              <Button onClick={addStaff} data-testid="ops-add-staff"><UserPlus className="mr-1.5 h-4 w-4" />Add</Button>
            </div>
            <div className="mt-4 space-y-2">
              {staff.map((s) => (
                <div key={s.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm" data-testid={`ops-staff-${s.id}`}>
                  <span className={`font-medium ${s.is_suspended ? "text-muted-foreground line-through" : "text-foreground"}`}>{s.name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">{s.is_suspended ? "Suspended" : "Active"}</span>
                    <Switch checked={!s.is_suspended} onCheckedChange={() => toggleSuspend(s)} data-testid={`ops-suspend-${s.id}`} />
                    <Button variant="ghost" size="icon" onClick={() => removeStaff(s)} data-testid={`ops-remove-${s.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="times" className="mt-6">
          {cfg && (
            <>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              {[["day_times", "Day times"], ["evening_times", "Evening times"], ["sunday_times", "Sunday times"], ["extra_times", "Extra time lengths"]].map(([field, title]) => (
                <Card key={field} className="p-5" data-testid={`ops-times-${field}`}>
                  <div className="mb-2 flex items-center justify-between"><span className="font-heading font-bold text-foreground">{title}</span><Button size="sm" variant="outline" onClick={() => editTimeList(field)} data-testid={`ops-add-time-${field}`}>Add</Button></div>
                  <div className="space-y-1.5">
                    {(cfg[field] || []).map((o, i) => (
                      <div key={o.label} className="flex items-center justify-between rounded-md border border-border px-2.5 py-1.5 text-sm">
                        <span>{o.label} <span className="text-muted-foreground">({o.hours}h)</span></span>
                        <Button variant="ghost" size="icon" onClick={() => removeTime(field, i)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                      </div>
                    ))}
                    {(cfg[field] || []).length === 0 && <p className="text-xs text-muted-foreground">None yet.</p>}
                  </div>
                </Card>
              ))}
            </div>

            <Card className="mt-4 p-5" data-testid="ops-manage-tills">
              <div className="mb-3 flex items-center gap-1.5"><Settings2 className="h-4 w-4 text-emerald-600" /><span className="font-heading font-bold text-foreground">Manage Tills</span></div>
              <div className="flex gap-2">
                <Input value={newTill} onChange={(e) => setNewTill(e.target.value)} placeholder="New till name (e.g. Till 5)" data-testid="ops-new-till" onKeyDown={(e) => e.key === "Enter" && addTill()} />
                <Button onClick={addTill} data-testid="ops-add-till"><UserPlus className="mr-1.5 h-4 w-4" />Add</Button>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">Editing or deleting a till does not change entries already logged; it only affects new logging.</p>
              <div className="mt-3 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {(cfg.tills || []).map((t, i) => (
                  <div key={t} className="flex items-center justify-between rounded-md border border-border px-2.5 py-1.5 text-sm" data-testid={`ops-till-${i}`}>
                    <span className="truncate">{t}</span>
                    <div className="flex shrink-0 items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => editTill(i)} data-testid={`ops-till-edit-${i}`}>Edit</Button>
                      <Button variant="ghost" size="icon" onClick={() => removeTill(i)} data-testid={`ops-till-del-${i}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
            </>
          )}
          {user.role === "super_admin" && (
            <Button className="mt-4" variant="outline" onClick={setPassword} data-testid="ops-set-password"><KeyRound className="mr-1.5 h-4 w-4" />Set this branch's desk password</Button>
          )}
        </TabsContent>

        {user.role === "super_admin" && weighting && (
          <TabsContent value="rules" className="mt-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {["weekday", "sunday"].map((scope) => (
                <Card key={scope} className="p-5">
                  <div className="mb-3 font-heading font-bold capitalize text-foreground">{scope} multipliers</div>
                  <div className="space-y-2">
                    {TILLS.map((t) => (
                      <div key={t} className="flex items-center justify-between gap-2 text-sm">
                        <span>{t}</span>
                        <Input type="number" step="0.1" className="w-24" value={weighting[scope]?.[t] ?? 0}
                          onChange={(e) => saveWeighting(scope, t, parseFloat(e.target.value) || 0)} data-testid={`weight-${scope}-${t}`} />
                      </div>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>
        )}
      </Tabs>
    </Layout>
  );
}
